import { useEffect, useState } from "react";
import { Calendar, Clock, Mail, User } from "lucide-react";
import { useParams } from "react-router-dom";

import { supabase } from "../../../lib/supabase";

interface Assignment {
  uuid: string;
  start: string;
  end: string | null;
  mentor: {
    name: string;
    email: string;
  };
  mentee: {
    name: string;
    email: string;
  };
}

export function AssignmentDetails() {
  const { id } = useParams();
  const [assignment, setAssignment] = useState<Assignment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchAssignment() {
      if (!id) return;

      try {
        const { data, error: fetchError } = await supabase
          .from("relationship_assignments")
          .select(
            `
            uuid,
            start,
            end,
            mentor:mentor_uuid (
              uuid,
              vincer_base_user (
                name,
                email
              )
            ),
            mentee:mentee_uuid (
              uuid,
              vincer_base_user (
                name,
                email
              )
            )
          `
          )
          .eq("uuid", id)
          .single();

        if (fetchError) throw fetchError;

        if (data) {
          const typedData = data as unknown as {
            uuid: string;
            start: string;
            end: string | null;
            mentor: {
              uuid: string;
              vincer_base_user: {
                name: string;
                email: string;
              };
            };
            mentee: {
              uuid: string;
              vincer_base_user: {
                name: string;
                email: string;
              };
            };
          };

          setAssignment({
            uuid: typedData.uuid,
            start: typedData.start,
            end: typedData.end,
            mentor: {
              name: typedData.mentor.vincer_base_user.name,
              email: typedData.mentor.vincer_base_user.email,
            },
            mentee: {
              name: typedData.mentee.vincer_base_user.name,
              email: typedData.mentee.vincer_base_user.email,
            },
          });
        }
      } catch (err) {
        console.error("Error fetching assignment:", err);
        setError("Failed to load assignment details");
      } finally {
        setLoading(false);
      }
    }

    fetchAssignment();
  }, [id]);

  const getStatus = () => {
    if (!assignment) return "unknown";
    return assignment.end ? "completed" : "active";
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800";
      case "completed":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl py-12 text-center">
        <div className="mx-auto mb-4 size-12 animate-spin rounded-full border-b-2 border-[#ffcc32]"></div>
        <p className="text-gray-600">Loading assignment details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl py-12 text-center">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  if (!assignment) {
    return (
      <div className="mx-auto max-w-4xl py-12 text-center">
        <p className="text-gray-600">Assignment not found</p>
      </div>
    );
  }

  const status = getStatus();

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-8 flex items-center justify-between">
        <h1 className="text-3xl font-bold">Assignment Details</h1>
        <span
          className={`rounded-full px-4 py-2 text-sm font-medium ${getStatusColor(status)}`}
        >
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      </div>

      <div className="grid gap-6">
        <div className="grid gap-6 md:grid-cols-2">
          <div className="rounded-xl border bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center gap-3">
              <User className="size-5 text-[#ffcc32]" />
              <h2 className="text-xl font-semibold">Mentor</h2>
            </div>
            <div className="space-y-2">
              <p className="text-lg font-medium">{assignment.mentor.name}</p>
              <p className="text-gray-600">{assignment.mentor.email}</p>
            </div>
          </div>

          <div className="rounded-xl border bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center gap-3">
              <User className="size-5 text-[#ffcc32]" />
              <h2 className="text-xl font-semibold">Mentee</h2>
            </div>
            <div className="space-y-2">
              <p className="text-lg font-medium">{assignment.mentee.name}</p>
              <p className="text-gray-600">{assignment.mentee.email}</p>
            </div>
          </div>
        </div>

        <div className="rounded-xl border bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center gap-3">
            <Calendar className="size-5 text-[#ffcc32]" />
            <h2 className="text-xl font-semibold">Assignment Details</h2>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <p className="mb-1 text-sm font-medium text-gray-500">Started</p>
              <p className="text-gray-900">
                {new Date(assignment.start).toLocaleDateString("en-US", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            </div>
            {assignment.end && (
              <div>
                <p className="mb-1 text-sm font-medium text-gray-500">
                  Completed
                </p>
                <p className="text-gray-900">
                  {new Date(assignment.end).toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-4">
          <button className="flex items-center gap-2 rounded-lg bg-[#ffcc32] px-6 py-3 text-black transition-colors hover:bg-[#ffd65c]">
            <Mail className="size-5" />
            <span>Send Message</span>
          </button>
          {!assignment.end && (
            <button className="flex items-center gap-2 rounded-lg bg-[#4CAF50] px-6 py-3 text-white transition-colors hover:bg-[#45a049]">
              <Clock className="size-5" />
              <span>Mark as Completed</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
